import { useMemo } from 'react';
import {
    FieldType,
    FormSplitConfiguration
} from '#types/System.types';
import { generateSplitOptions } from './generateSplitOptions';
import { formatSplitOptionsForUI } from './formatSplitOptionsForUI';
import { useArTableRowsForSplit } from './useArTableRowsForSplit';


/**
 * Hook to manage form splitting functionality.
 *
 * Form fields are NOT an input: the only data source is `useArTableRowsForSplit`,
 * which binds the split criteria's AR-table rows straight from Firestore. That is
 * what keeps split consumers insensitive to field-value keystrokes — do not
 * re-add a `fields` parameter.
 *
 * Each call mounts its own row listeners, so hold it once per surface that needs
 * the options (`FormSplitControl`, `WorkflowParameterSidebar`), never inside a
 * shared hook every consumer holds.
 *
 * @param splitConfig - Configuration defining splitting behavior
 * @param fieldTypes - Field types used to resolve each split criterion's label
 * @param arTableParentPath - `forms/{formId}/formVersions/{versionId}` of the AR tables
 * @param splitActive - Gates the Firestore listeners; false leaves them detached
 * @returns Object containing raw options, formatted options, metadata map, and row-limit warning
 */
export const useFormSplit = (
    splitConfig: FormSplitConfiguration | undefined,
    fieldTypes: Record<string, FieldType>,
    arTableParentPath?: string,
    splitActive: boolean = false
) => {
    const { rowsByTableId, splitRowLimitWarning } = useArTableRowsForSplit({
        splitConfig,
        arTableParentPath,
        enabled: splitActive
    });

    const splitOptions = useMemo(() => {
        return generateSplitOptions(splitConfig, fieldTypes, rowsByTableId);
    }, [splitConfig, fieldTypes, rowsByTableId]);

    const { options: formattedOptions, metadata: splitOptionsMetadata } = useMemo(() =>
        formatSplitOptionsForUI(splitOptions),
        [splitOptions]
    );

    return {
        splitOptions,
        formattedOptions,
        splitOptionsMetadata,
        splitRowLimitWarning
    };
};
